import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Save, Plus, Trash2, Edit, X, RefreshCw,
  BookOpen, Calendar, MapPin, Phone, Mail, Image,
  Eye, Heart, Zap, Music, Shield, Star, Clock
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for benefits
const iconMap = {
  Eye: Eye,
  Heart: Heart,
  Zap: Zap,
  Music: Music,
  Shield: Shield,
  Star: Star
};

const AdminKapoeiraEditor = () => {
  const [activeTab, setActiveTab] = useState('capoeira'); // 'capoeira' or 'book'
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Capoeira page state
  const [capoeiraContent, setCapoeiraContent] = useState(null);
  const [editingBenefit, setEditingBenefit] = useState(null);
  const [editingSchedule, setEditingSchedule] = useState(null);
  
  // Book page state
  const [bookContent, setBookContent] = useState(null);
  const [editingChapter, setEditingChapter] = useState(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [capoeiraRes, bookRes] = await Promise.all([
        fetch(`${API}/api/kapoeira/capoeira-page`),
        fetch(`${API}/api/kapoeira/book`)
      ]);
      
      if (capoeiraRes.ok) {
        const data = await capoeiraRes.json();
        setCapoeiraContent(data);
      }
      
      if (bookRes.ok) {
        const data = await bookRes.json();
        setBookContent(data);
      }
    } catch (err) {
      console.error('Failed to fetch content:', err);
      toast.error('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const saveCapoeiraContent = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/kapoeira/capoeira-page`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(capoeiraContent)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Capoeira page saved!');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const saveBookContent = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/kapoeira/book`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookContent)
      });
      
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Book page saved!');
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    if (!confirm('Reset all Kapoeira content to defaults?')) return;
    
    try {
      const res = await fetch(`${API}/api/kapoeira/seed`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to reset');
      toast.success('Content reset to defaults!');
      fetchContent();
    } catch (err) {
      toast.error('Failed to reset');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
          <BookOpen className="w-12 h-12 text-amber-500" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      {/* Header */}
      <div className="bg-stone-800 border-b border-stone-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-stone-700 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Kapoeira Editor</h1>
              <p className="text-sm text-muted-foreground">Manage Capoeira Angola & Kapoeira Book pages</p>
            </div>
          </div>
          <button
            onClick={resetToDefaults}
            className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset to Defaults
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-6 py-4">
        <div className="flex gap-2 border-b border-stone-700 mb-6">
          <button
            onClick={() => setActiveTab('capoeira')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'capoeira'
                ? 'border-amber-500 text-amber-500'
                : 'border-transparent text-muted-foreground hover:text-white'
            }`}
          >
            <Shield className="w-4 h-4 inline mr-2" />
            Capoeira Angola Page
          </button>
          <button
            onClick={() => setActiveTab('book')}
            className={`px-6 py-3 font-medium transition-colors border-b-2 -mb-px ${
              activeTab === 'book'
                ? 'border-amber-500 text-amber-500'
                : 'border-transparent text-muted-foreground hover:text-white'
            }`}
          >
            <BookOpen className="w-4 h-4 inline mr-2" />
            Kapoeira Book Page
          </button>
        </div>

        {/* CAPOEIRA TAB */}
        {activeTab === 'capoeira' && capoeiraContent && (
          <div className="space-y-8">
            {/* Hero Section */}
            <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Hero Section</h2>
                <Link to="/capoeira" target="_blank" className="text-sm text-amber-500 hover:underline">
                  View Live Page →
                </Link>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Badge Text</label>
                  <input
                    type="text"
                    value={capoeiraContent.hero?.badge || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      hero: { ...capoeiraContent.hero, badge: e.target.value }
                    })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={capoeiraContent.hero?.title || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      hero: { ...capoeiraContent.hero, title: e.target.value }
                    })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={capoeiraContent.hero?.subtitle || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      hero: { ...capoeiraContent.hero, subtitle: e.target.value }
                    })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={capoeiraContent.hero?.description || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      hero: { ...capoeiraContent.hero, description: e.target.value }
                    })}
                    rows={3}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Class Schedule */}
            <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Class Schedule</h2>
                <button
                  onClick={() => {
                    const newSchedule = [...(capoeiraContent.class_schedule || []), { day: '', time: '', type: '' }];
                    setCapoeiraContent({ ...capoeiraContent, class_schedule: newSchedule });
                  }}
                  className="px-3 py-1 bg-amber-500 text-black rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  Add Class
                </button>
              </div>
              
              <div className="space-y-3">
                {capoeiraContent.class_schedule?.map((cls, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-stone-700/50 rounded-lg p-3">
                    <Calendar className="w-5 h-5 text-amber-500" />
                    <input
                      type="text"
                      value={cls.day}
                      onChange={(e) => {
                        const updated = [...capoeiraContent.class_schedule];
                        updated[idx].day = e.target.value;
                        setCapoeiraContent({ ...capoeiraContent, class_schedule: updated });
                      }}
                      placeholder="Day"
                      className="flex-1 px-3 py-1 bg-stone-600 rounded border border-stone-500 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={cls.time}
                      onChange={(e) => {
                        const updated = [...capoeiraContent.class_schedule];
                        updated[idx].time = e.target.value;
                        setCapoeiraContent({ ...capoeiraContent, class_schedule: updated });
                      }}
                      placeholder="Time"
                      className="flex-1 px-3 py-1 bg-stone-600 rounded border border-stone-500 focus:border-amber-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={cls.type}
                      onChange={(e) => {
                        const updated = [...capoeiraContent.class_schedule];
                        updated[idx].type = e.target.value;
                        setCapoeiraContent({ ...capoeiraContent, class_schedule: updated });
                      }}
                      placeholder="Type"
                      className="flex-1 px-3 py-1 bg-stone-600 rounded border border-stone-500 focus:border-amber-500 focus:outline-none"
                    />
                    <button
                      onClick={() => {
                        const updated = capoeiraContent.class_schedule.filter((_, i) => i !== idx);
                        setCapoeiraContent({ ...capoeiraContent, class_schedule: updated });
                      }}
                      className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
              <h2 className="text-xl font-bold mb-4">Contact Information</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <MapPin className="w-4 h-4 inline mr-1" /> Location
                  </label>
                  <input
                    type="text"
                    value={capoeiraContent.contact?.location || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      contact: { ...capoeiraContent.contact, location: e.target.value }
                    })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <Phone className="w-4 h-4 inline mr-1" /> Phone
                  </label>
                  <input
                    type="text"
                    value={capoeiraContent.contact?.phone || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      contact: { ...capoeiraContent.contact, phone: e.target.value }
                    })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    <Mail className="w-4 h-4 inline mr-1" /> Email
                  </label>
                  <input
                    type="text"
                    value={capoeiraContent.contact?.email || ''}
                    onChange={(e) => setCapoeiraContent({
                      ...capoeiraContent,
                      contact: { ...capoeiraContent.contact, email: e.target.value }
                    })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveCapoeiraContent}
                disabled={saving}
                className="px-6 py-3 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Capoeira Page'}
              </button>
            </div>
          </div>
        )}

        {/* BOOK TAB */}
        {activeTab === 'book' && bookContent && (
          <div className="space-y-8">
            {/* Book Details */}
            <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Book Details</h2>
                <Link to="/kapoeira-book" target="_blank" className="text-sm text-amber-500 hover:underline">
                  View Live Page →
                </Link>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={bookContent.title || ''}
                    onChange={(e) => setBookContent({ ...bookContent, title: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Subtitle</label>
                  <input
                    type="text"
                    value={bookContent.subtitle || ''}
                    onChange={(e) => setBookContent({ ...bookContent, subtitle: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Author</label>
                  <input
                    type="text"
                    value={bookContent.author || ''}
                    onChange={(e) => setBookContent({ ...bookContent, author: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Price ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={bookContent.price || 0}
                    onChange={(e) => setBookContent({ ...bookContent, price: parseFloat(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={bookContent.description || ''}
                    onChange={(e) => setBookContent({ ...bookContent, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none resize-none"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1">Cover Image URL</label>
                  <input
                    type="text"
                    value={bookContent.cover_image || ''}
                    onChange={(e) => setBookContent({ ...bookContent, cover_image: e.target.value })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>

            {/* Book Chapters */}
            <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Chapters</h2>
                <button
                  onClick={() => {
                    const newChapters = [...(bookContent.chapters || []), { 
                      number: (bookContent.chapters?.length || 0) + 1, 
                      title: '', 
                      subtitle: '', 
                      start_page: 1, 
                      is_preview: false 
                    }];
                    setBookContent({ ...bookContent, chapters: newChapters });
                  }}
                  className="px-3 py-1 bg-amber-500 text-black rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  Add Chapter
                </button>
              </div>
              
              <div className="space-y-3">
                {bookContent.chapters?.map((chapter, idx) => (
                  <div key={idx} className="bg-stone-700/50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-500 font-bold">
                        {chapter.number}
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          value={chapter.title}
                          onChange={(e) => {
                            const updated = [...bookContent.chapters];
                            updated[idx].title = e.target.value;
                            setBookContent({ ...bookContent, chapters: updated });
                          }}
                          placeholder="Chapter Title"
                          className="px-3 py-1 bg-stone-600 rounded border border-stone-500 focus:border-amber-500 focus:outline-none"
                        />
                        <input
                          type="text"
                          value={chapter.subtitle}
                          onChange={(e) => {
                            const updated = [...bookContent.chapters];
                            updated[idx].subtitle = e.target.value;
                            setBookContent({ ...bookContent, chapters: updated });
                          }}
                          placeholder="Subtitle"
                          className="px-3 py-1 bg-stone-600 rounded border border-stone-500 focus:border-amber-500 focus:outline-none"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={chapter.start_page}
                          onChange={(e) => {
                            const updated = [...bookContent.chapters];
                            updated[idx].start_page = parseInt(e.target.value) || 1;
                            setBookContent({ ...bookContent, chapters: updated });
                          }}
                          placeholder="Page"
                          className="w-20 px-2 py-1 bg-stone-600 rounded border border-stone-500 focus:border-amber-500 focus:outline-none text-center"
                        />
                        <label className="flex items-center gap-1 text-sm">
                          <input
                            type="checkbox"
                            checked={chapter.is_preview}
                            onChange={(e) => {
                              const updated = [...bookContent.chapters];
                              updated[idx].is_preview = e.target.checked;
                              setBookContent({ ...bookContent, chapters: updated });
                            }}
                            className="rounded"
                          />
                          Preview
                        </label>
                        <button
                          onClick={() => {
                            const updated = bookContent.chapters.filter((_, i) => i !== idx);
                            setBookContent({ ...bookContent, chapters: updated });
                          }}
                          className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Book Features */}
            <div className="bg-stone-800 rounded-xl p-6 border border-stone-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Features</h2>
                <button
                  onClick={() => {
                    const newFeatures = [...(bookContent.features || []), ''];
                    setBookContent({ ...bookContent, features: newFeatures });
                  }}
                  className="px-3 py-1 bg-amber-500 text-black rounded-lg text-sm font-medium"
                >
                  <Plus className="w-4 h-4 inline mr-1" />
                  Add Feature
                </button>
              </div>
              
              <div className="space-y-2">
                {bookContent.features?.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-amber-500" />
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const updated = [...bookContent.features];
                        updated[idx] = e.target.value;
                        setBookContent({ ...bookContent, features: updated });
                      }}
                      className="flex-1 px-3 py-1 bg-stone-700 rounded border border-stone-600 focus:border-amber-500 focus:outline-none"
                    />
                    <button
                      onClick={() => {
                        const updated = bookContent.features.filter((_, i) => i !== idx);
                        setBookContent({ ...bookContent, features: updated });
                      }}
                      className="p-1 text-red-400 hover:bg-red-500/20 rounded"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveBookContent}
                disabled={saving}
                className="px-6 py-3 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Saving...' : 'Save Book Page'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminKapoeiraEditor;
